/**
 * Admin Sales Tax Page - City-based tax rate management
 * 
 * Features:
 * - City + State based tax rates
 * - Effective date for rate changes
 * - Expired flag for historical rates
 * - Single default rate enforcement
 * - Rate display as percentage (stored as decimal)
 */
import React, { useState, useEffect, useCallback } from 'react';
import { taxAPI } from '../../utils/api';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Checkbox } from '../../components/ui/checkbox';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../../components/ui/alert-dialog';
import { toast } from 'sonner';
import {
  Loader2,
  Plus,
  Pencil,
  Trash2,
  Star,
  AlertTriangle,
  MapPin,
  Percent,
  Save,
  Building2,
  Calendar,
  Archive,
} from 'lucide-react';

// US State codes
const US_STATES = [
  { code: 'AL', name: 'Alabama' }, { code: 'AK', name: 'Alaska' }, { code: 'AZ', name: 'Arizona' },
  { code: 'AR', name: 'Arkansas' }, { code: 'CA', name: 'California' }, { code: 'CO', name: 'Colorado' },
  { code: 'CT', name: 'Connecticut' }, { code: 'DE', name: 'Delaware' }, { code: 'FL', name: 'Florida' },
  { code: 'GA', name: 'Georgia' }, { code: 'HI', name: 'Hawaii' }, { code: 'ID', name: 'Idaho' },
  { code: 'IL', name: 'Illinois' }, { code: 'IN', name: 'Indiana' }, { code: 'IA', name: 'Iowa' },
  { code: 'KS', name: 'Kansas' }, { code: 'KY', name: 'Kentucky' }, { code: 'LA', name: 'Louisiana' },
  { code: 'ME', name: 'Maine' }, { code: 'MD', name: 'Maryland' }, { code: 'MA', name: 'Massachusetts' },
  { code: 'MI', name: 'Michigan' }, { code: 'MN', name: 'Minnesota' }, { code: 'MS', name: 'Mississippi' },
  { code: 'MO', name: 'Missouri' }, { code: 'MT', name: 'Montana' }, { code: 'NE', name: 'Nebraska' },
  { code: 'NV', name: 'Nevada' }, { code: 'NH', name: 'New Hampshire' }, { code: 'NJ', name: 'New Jersey' },
  { code: 'NM', name: 'New Mexico' }, { code: 'NY', name: 'New York' }, { code: 'NC', name: 'North Carolina' },
  { code: 'ND', name: 'North Dakota' }, { code: 'OH', name: 'Ohio' }, { code: 'OK', name: 'Oklahoma' },
  { code: 'OR', name: 'Oregon' }, { code: 'PA', name: 'Pennsylvania' }, { code: 'RI', name: 'Rhode Island' },
  { code: 'SC', name: 'South Carolina' }, { code: 'SD', name: 'South Dakota' }, { code: 'TN', name: 'Tennessee' },
  { code: 'TX', name: 'Texas' }, { code: 'UT', name: 'Utah' }, { code: 'VT', name: 'Vermont' },
  { code: 'VA', name: 'Virginia' }, { code: 'WA', name: 'Washington' }, { code: 'WV', name: 'West Virginia' },
  { code: 'WI', name: 'Wisconsin' }, { code: 'WY', name: 'Wyoming' }, { code: 'DC', name: 'District of Columbia' },
  { code: 'NA', name: 'Not Applicable' },
];

// ===================================================
// MAIN COMPONENT
// ===================================================

export default function AdminSalesTax() {
  // Data states
  const [salesTaxes, setSalesTaxes] = useState([]);
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showExpired, setShowExpired] = useState(false);
  
  // Modal states
  const [showModal, setShowModal] = useState(false);
  const [editingTax, setEditingTax] = useState(null);
  const [saving, setSaving] = useState(false);
  
  // Delete confirmation
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  
  // Form state
  const [formData, setFormData] = useState({
    city: '',
    state: 'CA',
    rate: '',
    effective_date: '',
    is_default: false,
    is_expired: false,
  });

  // Fetch data
  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const [taxesRes, settingsRes] = await Promise.all([
        taxAPI.listZones(showExpired),
        taxAPI.getSettings(),
      ]);
      setSalesTaxes(taxesRes.items || []);
      setSettings(settingsRes);
    } catch (err) {
      toast.error('Failed to load sales tax data');
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [showExpired]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Open modal for create/edit
  const openModal = (tax = null) => {
    if (tax) {
      setEditingTax(tax);
      setFormData({
        city: tax.city || tax.name || '',
        state: tax.state || 'CA',
        rate: ((tax.rate || 0) * 100).toString(),
        effective_date: tax.effective_date || '',
        is_default: tax.is_default || false,
        is_expired: tax.is_expired || false,
      });
    } else {
      setEditingTax(null);
      setFormData({
        city: '',
        state: 'CA',
        rate: '',
        effective_date: new Date().toISOString().split('T')[0],
        is_default: false,
        is_expired: false,
      });
    }
    setShowModal(true);
  };

  // Save sales tax
  const handleSave = async () => {
    if (!formData.city.trim()) {
      toast.error('City is required');
      return;
    }
    
    const rateValue = parseFloat(formData.rate);
    if (isNaN(rateValue) || rateValue < 0 || rateValue > 100) {
      toast.error('Rate must be between 0 and 100');
      return;
    }
    
    setSaving(true);
    try {
      const payload = {
        city: formData.city.trim(),
        state: formData.state,
        rate: rateValue / 100, // Convert percentage to decimal
        effective_date: formData.effective_date || null,
        is_default: formData.is_default,
        is_expired: formData.is_expired,
      };
      
      if (editingTax) {
        await taxAPI.updateZone(editingTax.id, payload);
        toast.success('Sales tax rate updated');
      } else {
        await taxAPI.createZone(payload);
        toast.success('Sales tax rate created');
      }
      
      setShowModal(false);
      fetchData();
    } catch (err) {
      toast.error(err.message || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  // Delete sales tax
  const handleDelete = async (tax) => {
    try {
      await taxAPI.deleteZone(tax.id);
      toast.success('Sales tax rate deleted');
      setDeleteConfirm(null);
      fetchData();
    } catch (err) {
      toast.error(err.message || 'Failed to delete');
    }
  };

  // Set as default
  const handleSetDefault = async (tax) => {
    try {
      await taxAPI.setDefaultZone(tax.id);
      toast.success(`${tax.city}, ${tax.state} set as default`);
      fetchData();
    } catch (err) {
      toast.error(err.message || 'Failed to set default');
    }
  };

  // Mark as expired
  const handleExpire = async (tax) => {
    try {
      await taxAPI.expireZone(tax.id);
      toast.success(`${tax.city}, ${tax.state} marked as expired`);
      fetchData();
    } catch (err) {
      toast.error(err.message || 'Failed to expire');
    }
  };

  // Save business state
  const handleSaveBusinessState = async (state) => {
    try {
      await taxAPI.updateSettings({ business_state: state });
      toast.success('Business state updated');
      fetchData();
    } catch (err) {
      toast.error(err.message || 'Failed to update');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-gray-400" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="admin-sales-tax">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">Sales Tax</h1>
          <p className="text-gray-500 text-sm">Manage city-based tax rates</p>
        </div>
        <Button onClick={() => openModal()} data-testid="add-sales-tax-btn">
          <Plus className="w-4 h-4 mr-2" />
          Add Tax Rate
        </Button>
      </div>

      {/* Business State Setting */}
      <div className="bg-white rounded-xl border p-6">
        <div className="flex items-center gap-2 mb-4">
          <Building2 className="w-5 h-5 text-gray-400" />
          <h2 className="text-lg font-semibold">Business Location</h2>
        </div>
        <div className="flex items-center gap-4">
          <Label className="text-sm text-gray-500">Business State:</Label>
          <Select
            value={settings?.business_state || 'CA'}
            onValueChange={handleSaveBusinessState}
          >
            <SelectTrigger className="w-48">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {US_STATES.filter(s => s.code !== 'NA').map(s => (
                <SelectItem key={s.code} value={s.code}>{s.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <span className="text-xs text-gray-400">Used for pickup orders</span>
        </div>
      </div>

      {/* Sales Tax Table */}
      <div className="bg-white rounded-xl border">
        <div className="p-4 border-b flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Percent className="w-5 h-5 text-gray-400" />
            <h2 className="text-lg font-semibold">Tax Rates</h2>
            <Badge variant="outline">{salesTaxes.length}</Badge>
          </div>
          <div className="flex items-center gap-2">
            <Checkbox
              id="show-expired"
              checked={showExpired}
              onCheckedChange={setShowExpired}
            />
            <Label htmlFor="show-expired" className="text-sm cursor-pointer">
              Show expired rates
            </Label>
          </div>
        </div>

        {salesTaxes.length === 0 ? (
          <div className="p-8 text-center text-gray-500">
            <MapPin className="w-12 h-12 mx-auto mb-4 opacity-50" />
            <p>No sales tax rates configured</p>
            <p className="text-sm mt-1">Click "Add Tax Rate" to create one</p>
          </div>
        ) : (
          <table className="w-full">
            <thead className="bg-gray-50 text-xs text-gray-500 uppercase">
              <tr>
                <th className="px-4 py-3 text-left">City</th>
                <th className="px-4 py-3 text-left">State</th>
                <th className="px-4 py-3 text-right">Rate</th>
                <th className="px-4 py-3 text-left">Effective Date</th>
                <th className="px-4 py-3 text-center">Status</th>
                <th className="px-4 py-3 text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y">
              {salesTaxes.map(tax => (
                <tr 
                  key={tax.id} 
                  className={`hover:bg-gray-50 ${tax.is_expired ? 'opacity-50' : ''}`}
                  data-testid={`tax-row-${tax.id}`}
                >
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2">
                      <span className="font-medium">{tax.city || tax.name}</span>
                      {tax.is_default && (
                        <Badge className="bg-yellow-100 text-yellow-700 text-xs">
                          <Star className="w-3 h-3 mr-1" />
                          Default
                        </Badge>
                      )}
                    </div>
                  </td>
                  <td className="px-4 py-3 text-gray-600">{tax.state || 'CA'}</td>
                  <td className="px-4 py-3 text-right font-mono">
                    {((tax.rate || 0) * 100).toFixed(2)}%
                  </td>
                  <td className="px-4 py-3 text-gray-500 text-sm">
                    {tax.effective_date || '—'}
                  </td>
                  <td className="px-4 py-3 text-center">
                    {tax.is_expired ? (
                      <Badge variant="outline" className="text-gray-500">
                        <Archive className="w-3 h-3 mr-1" />
                        Expired
                      </Badge>
                    ) : (
                      <Badge className="bg-green-100 text-green-700">Active</Badge>
                    )}
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center justify-end gap-1">
                      {!tax.is_default && !tax.is_expired && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleSetDefault(tax)}
                          title="Set as default"
                        >
                          <Star className="w-4 h-4" />
                        </Button>
                      )}
                      {!tax.is_expired && !tax.is_default && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleExpire(tax)}
                          title="Mark as expired"
                        >
                          <Archive className="w-4 h-4" />
                        </Button>
                      )}
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => openModal(tax)}
                        title="Edit"
                      >
                        <Pencil className="w-4 h-4" />
                      </Button>
                      {!tax.is_default && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => setDeleteConfirm(tax)}
                          className="text-red-500 hover:text-red-600"
                          title="Delete"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Create/Edit Modal */}
      <Dialog open={showModal} onOpenChange={setShowModal}>
        <DialogContent className="max-w-md" data-testid="sales-tax-modal">
          <DialogHeader>
            <DialogTitle>
              {editingTax ? 'Edit Sales Tax Rate' : 'Add Sales Tax Rate'}
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>City <span className="text-red-500">*</span></Label>
                <Input
                  value={formData.city}
                  onChange={(e) => setFormData({ ...formData, city: e.target.value })}
                  placeholder="e.g., Irvine"
                  data-testid="tax-city-input"
                />
              </div>
              <div>
                <Label>State</Label>
                <Select
                  value={formData.state}
                  onValueChange={(v) => setFormData({ ...formData, state: v })}
                >
                  <SelectTrigger data-testid="tax-state-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {US_STATES.map(s => (
                      <SelectItem key={s.code} value={s.code}>{s.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Rate (%) <span className="text-red-500">*</span></Label>
                <div className="relative">
                  <Input
                    type="number"
                    min="0"
                    max="100"
                    step="0.01"
                    value={formData.rate}
                    onChange={(e) => setFormData({ ...formData, rate: e.target.value })}
                    placeholder="7.75"
                    className="pr-8"
                    data-testid="tax-rate-input"
                  />
                  <span className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400">%</span>
                </div>
              </div>
              <div>
                <Label>Effective Date</Label>
                <Input
                  type="date"
                  value={formData.effective_date}
                  onChange={(e) => setFormData({ ...formData, effective_date: e.target.value })}
                  data-testid="tax-date-input"
                />
              </div>
            </div>
            
            <div className="flex items-center gap-6 pt-2">
              <div className="flex items-center gap-2">
                <Checkbox
                  id="is-default"
                  checked={formData.is_default}
                  onCheckedChange={(v) => setFormData({ ...formData, is_default: v })}
                />
                <Label htmlFor="is-default" className="cursor-pointer">Set as default</Label>
              </div>
              <div className="flex items-center gap-2">
                <Checkbox
                  id="is-expired"
                  checked={formData.is_expired}
                  onCheckedChange={(v) => setFormData({ ...formData, is_expired: v })}
                />
                <Label htmlFor="is-expired" className="cursor-pointer">Expired</Label>
              </div>
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowModal(false)}>
              Cancel
            </Button>
            <Button onClick={handleSave} disabled={saving} data-testid="save-tax-btn">
              {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
              {editingTax ? 'Update' : 'Create'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <AlertDialog open={!!deleteConfirm} onOpenChange={() => setDeleteConfirm(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-red-500" />
              Delete Sales Tax Rate?
            </AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete the tax rate for{' '}
              <strong>{deleteConfirm?.city}, {deleteConfirm?.state}</strong>?
              This action cannot be undone. Consider marking it as expired instead.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => handleDelete(deleteConfirm)}
              className="bg-red-600 hover:bg-red-700"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

// Legacy export for backwards compatibility
export { AdminSalesTax as AdminTaxZones };
